/**
 * LocalDatabase - IndexedDB wrapper using Dexie.js
 * 
 * Platform-agnostic local database for offline-first architecture.
 * Web: Dexie.js (IndexedDB)
 * Future RN: Swap for WatermelonDB/SQLite
 */

import Dexie, { type Table } from 'dexie';

export interface LocalExpense {
  id: string;
  user_id: string;
  amount: number;
  currency: string;
  merchant: string;
  category: string;
  note: string | null;
  expense_date: string;
  tag: string | null;
  synced: boolean;
  created_at: string;
  updated_at: string;
  /** Dirty = modified locally, needs push to server */
  _dirty: boolean;
  /** Soft-deleted locally, pending server delete */
  _deleted: boolean;
}

export interface LocalIncome {
  id: string;
  user_id: string;
  amount: number;
  currency: string;
  source: string;
  category: string | null;
  income_date: string;
  is_recurring: boolean;
  recurrence_interval: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
  _dirty: boolean;
  _deleted: boolean;
}

export interface SyncMeta {
  id: string; // 'expenses' | 'income'
  lastPullCursor: string; // ISO timestamp of last pulled updated_at
  lastPushAt: string;
}

export interface CachedBudgetSettings {
  user_id: string;
  monthly_budget: number;
  currency: string;
  updated_at: string;
}

class CasheeDatabase extends Dexie {
  expenses!: Table<LocalExpense, string>;
  income!: Table<LocalIncome, string>;
  syncMeta!: Table<SyncMeta, string>;
  budgetSettings!: Table<CachedBudgetSettings, string>;

  constructor() {
    super('cashee-local');

    this.version(1).stores({
      expenses: 'id, user_id, expense_date, updated_at, [user_id+expense_date], [user_id+_dirty]',
      income: 'id, user_id, income_date, updated_at, [user_id+income_date], [user_id+_dirty]',
      syncMeta: 'id',
    });

    this.version(2).stores({
      expenses: 'id, user_id, expense_date, updated_at, [user_id+expense_date], [user_id+_dirty]',
      income: 'id, user_id, income_date, updated_at, [user_id+income_date], [user_id+_dirty]',
      syncMeta: 'id',
      budgetSettings: 'user_id',
    });
  }
}

// Singleton instance
const db = new CasheeDatabase();

export const localDatabase = {
  // ─── Expenses ───────────────────────────────────────────

  async putExpense(expense: Omit<LocalExpense, '_dirty' | '_deleted'>): Promise<void> {
    await db.expenses.put({
      ...expense,
      _dirty: true,
      _deleted: false,
    });
  },

  async getExpenses(userId: string, monthStart: string, monthEnd: string): Promise<LocalExpense[]> {
    return db.expenses
      .where('[user_id+expense_date]')
      .between([userId, monthStart], [userId, monthEnd], true, true)
      .filter(e => !e._deleted)
      .toArray();
  },

  async getRecentExpenses(userId: string, limit: number = 7): Promise<LocalExpense[]> {
    const all = await db.expenses
      .where('user_id')
      .equals(userId)
      .filter(e => !e._deleted)
      .toArray();
    // Sort by expense_date desc, then created_at desc
    all.sort((a, b) => {
      const dateCmp = b.expense_date.localeCompare(a.expense_date);
      if (dateCmp !== 0) return dateCmp;
      return b.created_at.localeCompare(a.created_at);
    });
    return all.slice(0, limit);
  },

  async getExpenseById(id: string): Promise<LocalExpense | undefined> {
    return db.expenses.get(id);
  },

  async deleteExpense(id: string): Promise<void> {
    const existing = await db.expenses.get(id);
    if (existing) {
      // Mark as dirty + deleted so sync engine pushes the delete
      await db.expenses.update(id, { _dirty: true, _deleted: true, updated_at: new Date().toISOString() });
    }
  },

  async updateExpense(id: string, changes: Partial<LocalExpense>): Promise<void> {
    await db.expenses.update(id, {
      ...changes,
      _dirty: true,
      updated_at: new Date().toISOString(),
    });
  },

  async getUnsyncedExpenses(userId: string): Promise<LocalExpense[]> {
    return db.expenses
      .where('[user_id+_dirty]')
      .equals([userId, 1]) // Dexie stores booleans as 0/1
      .toArray();
  },

  async bulkPutExpenses(expenses: LocalExpense[]): Promise<void> {
    await db.expenses.bulkPut(expenses);
  },

  async markExpenseSynced(id: string): Promise<void> {
    await db.expenses.update(id, { _dirty: false, synced: true });
  },

  async removeExpensePermanently(id: string): Promise<void> {
    await db.expenses.delete(id);
  },

  // ─── Income ─────────────────────────────────────────────

  async putIncome(income: Omit<LocalIncome, '_dirty' | '_deleted'>): Promise<void> {
    await db.income.put({
      ...income,
      _dirty: true,
      _deleted: false,
    });
  },

  async getIncome(userId: string, monthStart: string, monthEnd: string): Promise<LocalIncome[]> {
    return db.income
      .where('[user_id+income_date]')
      .between([userId, monthStart], [userId, monthEnd], true, true)
      .filter(i => !i._deleted)
      .toArray();
  },

  async deleteIncome(id: string): Promise<void> {
    const existing = await db.income.get(id);
    if (existing) {
      await db.income.update(id, { _dirty: true, _deleted: true, updated_at: new Date().toISOString() });
    }
  },

  async updateIncome(id: string, changes: Partial<LocalIncome>): Promise<void> {
    await db.income.update(id, {
      ...changes,
      _dirty: true,
      updated_at: new Date().toISOString(),
    });
  },

  async getUnsyncedIncome(userId: string): Promise<LocalIncome[]> {
    return db.income
      .where('[user_id+_dirty]')
      .equals([userId, 1])
      .toArray();
  },

  async bulkPutIncome(income: LocalIncome[]): Promise<void> {
    await db.income.bulkPut(income);
  },

  async markIncomeSynced(id: string): Promise<void> {
    await db.income.update(id, { _dirty: false });
  },

  async removeIncomePermanently(id: string): Promise<void> {
    await db.income.delete(id);
  },

  // ─── Sync Meta ──────────────────────────────────────────

  async getSyncMeta(table: string): Promise<SyncMeta | undefined> {
    return db.syncMeta.get(table);
  },

  async setSyncMeta(meta: SyncMeta): Promise<void> {
    await db.syncMeta.put(meta);
  },

  // ─── Utilities ──────────────────────────────────────────

  // ─── Budget Settings Cache ───────────────────────────────

  async getCachedBudgetSettings(userId: string): Promise<CachedBudgetSettings | undefined> {
    return db.budgetSettings.get(userId);
  },

  async setCachedBudgetSettings(settings: CachedBudgetSettings): Promise<void> {
    await db.budgetSettings.put(settings);
  },

  async clearAll(): Promise<void> {
    await Promise.all([
      db.expenses.clear(),
      db.income.clear(),
      db.syncMeta.clear(),
      db.budgetSettings.clear(),
    ]);
  },

  async requestPersistentStorage(): Promise<boolean> {
    if (navigator.storage?.persist) {
      return navigator.storage.persist();
    }
    return false;
  },

  /** Migrate old localStorage queue items into IndexedDB */
  async migrateFromLocalStorage(): Promise<number> {
    const QUEUE_KEY = 'cashee_expense_queue';
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return 0;

    try {
      const items = JSON.parse(raw) as Array<{
        action: string;
        data: Record<string, unknown>;
        table?: string;
      }>;

      let migrated = 0;
      for (const item of items) {
        if (item.action === 'insert' && item.data.user_id) {
          const table = (item.table || 'expenses') as 'expenses' | 'income';
          const now = new Date().toISOString();

          if (table === 'expenses') {
            await db.expenses.put({
              id: (item.data.id as string) || crypto.randomUUID(),
              user_id: item.data.user_id as string,
              amount: Number(item.data.amount),
              currency: (item.data.currency as string) || 'EUR',
              merchant: (item.data.merchant as string) || '',
              category: (item.data.category as string) || 'Bills/Other',
              note: (item.data.note as string) || null,
              expense_date: (item.data.expense_date as string) || now.split('T')[0],
              tag: (item.data.tag as string) || null,
              synced: false,
              created_at: now,
              updated_at: now,
              _dirty: true,
              _deleted: false,
            });
            migrated++;
          } else if (table === 'income') {
            await db.income.put({
              id: (item.data.id as string) || crypto.randomUUID(),
              user_id: item.data.user_id as string,
              amount: Number(item.data.amount),
              currency: (item.data.currency as string) || 'EUR',
              source: (item.data.source as string) || '',
              category: (item.data.category as string) || null,
              income_date: (item.data.income_date as string) || now.split('T')[0],
              is_recurring: false,
              recurrence_interval: null,
              note: (item.data.note as string) || null,
              created_at: now,
              updated_at: now,
              _dirty: true,
              _deleted: false,
            });
            migrated++;
          }
        }
      }

      // Clear old queue after successful migration
      if (migrated > 0) {
        localStorage.removeItem(QUEUE_KEY);
        console.log(`[LocalDB] Migrated ${migrated} items from localStorage`);
      }

      return migrated;
    } catch (err) {
      console.error('[LocalDB] Migration from localStorage failed:', err);
      return 0;
    }
  },
};

export default db;
